import React from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid } from "@/components/nzDataGrid"
import type { Organization } from "@/models/organization"
import {
  DataGridRecordCount,
  DataGridToolbar,
  baseColumnDef,
} from "@/nzds/data-grid"
import { useBillHistory } from "@/services/bill/use-bill-history"
import type { Bill, BillHistoryEntryResponse } from "@/services/core-api/types"
import { useCostColumn } from "@/utils/grid-columns/use-cost-column"
import { useFlowDirectionColumn } from "@/utils/grid-columns/use-flow-direction-column"
import { useUsageColumn } from "@/utils/grid-columns/use-usage-column"
import { getDisplayValue, getStringValue } from "@/utils/value"
import type { TFunction } from "i18next"
import { omit } from "lodash-es"

import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import type { GridColDef, GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

interface BillHistorySectionProps {
  billDetails: Bill | null | undefined
  billId: number | null
  organizationId: Organization["id"] | undefined
}

interface BillHistoryGridRow {
  id: number
  cost: string | null
  currency: string | null
  end_date: string | null
  flow_direction: string | null
  source_captured_at: string | null
  source_type: string | null
  start_date: string | null
  usage: string | null
  usage_unit: string | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const getBillSnapshot = (
  entry: BillHistoryEntryResponse
): Record<string, unknown> => {
  if (!isRecord(entry.previous_values)) {
    return {}
  }

  const nestedBill = entry.previous_values.bill
  if (isRecord(nestedBill)) {
    return nestedBill
  }

  return entry.previous_values
}

const parseTimestamp = (value: string | null): number => {
  if (!value) {
    return Number.NEGATIVE_INFINITY
  }

  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

const toHistoryGridRow = (
  entry: BillHistoryEntryResponse,
  billDetails: Bill | null | undefined
): BillHistoryGridRow => {
  const snapshot = getBillSnapshot(entry)
  const sourceCapturedAt =
    getStringValue(snapshot.source_captured_at) ??
    getStringValue(entry.modified_at)

  return {
    id: entry.id,
    cost: getStringValue(snapshot.cost),
    currency: getStringValue(snapshot.currency),
    end_date:
      getStringValue(snapshot.end_date) ?? billDetails?.end_date ?? null,
    flow_direction:
      getStringValue(snapshot.flow_direction) ??
      billDetails?.flow_direction ??
      null,
    source_captured_at: sourceCapturedAt,
    source_type:
      getStringValue(snapshot.source_type) ?? billDetails?.source_type ?? null,
    start_date:
      getStringValue(snapshot.start_date) ?? billDetails?.start_date ?? null,
    usage: getStringValue(snapshot.usage),
    usage_unit: getStringValue(snapshot.usage_unit),
  }
}

const buildHistoryRows = (
  entries: BillHistoryEntryResponse[] | undefined,
  billDetails: Bill | null | undefined
): BillHistoryGridRow[] =>
  (entries ?? [])
    .map((entry) => toHistoryGridRow(entry, billDetails))
    .sort(
      (left, right) =>
        parseTimestamp(right.source_captured_at) -
        parseTimestamp(left.source_captured_at)
    )

const buildHistoryColumns = (
  t: TFunction<"common">,
  costColumn: GridColDef<BillHistoryGridRow>,
  flowDirectionColumn: GridColDef<BillHistoryGridRow>,
  usageColumn: GridColDef<BillHistoryGridRow>
): GridColDef<BillHistoryGridRow>[] => [
  {
    ...baseColumnDef,
    field: "source_type",
    filterable: false,
    flex: 1,
    headerName: t("fields.sourceType.title"),
    minWidth: 120,
    sortable: false,
    valueFormatter: (value) => getDisplayValue(value),
  },
  {
    ...baseColumnDef,
    field: "start_date",
    filterable: false,
    headerName: t("bill.periodStart"),
    minWidth: 120,
    sortable: false,
    valueFormatter: (value) => getDisplayValue(value),
    width: 120,
  },
  {
    ...baseColumnDef,
    field: "end_date",
    filterable: false,
    headerName: t("bill.periodEnd"),
    minWidth: 120,
    sortable: false,
    valueFormatter: (value) => getDisplayValue(value),
    width: 120,
  },
  costColumn,
  {
    ...omit(usageColumn, "maxWidth"),
    sortable: false,
  },
  {
    ...flowDirectionColumn,
    sortable: false,
  },
]

export const BillHistorySection: React.FC<BillHistorySectionProps> = ({
  billDetails,
  billId,
  organizationId,
}) => {
  const { t } = useTranslation("common")
  const costColumn = useCostColumn<BillHistoryGridRow>({
    sortable: false,
  })
  const flowDirectionColumn = useFlowDirectionColumn<BillHistoryGridRow>()
  const usageColumn = useUsageColumn<BillHistoryGridRow>({
    convert: false,
    precision: 2,
  })
  const {
    billsData,
    billsError,
    billsIsFetching,
    billsIsLoading,
    billsRefetch,
  } = useBillHistory({
    billId,
    organizationId,
    queryOptions: {
      enabled: billId !== null,
    },
  })

  const historyRows = buildHistoryRows(billsData, billDetails)
  const historyColumns = buildHistoryColumns(
    t,
    costColumn,
    flowDirectionColumn,
    usageColumn
  )
  const hasBillHistoryError = Boolean(billsError)
  const historyGridLoading =
    (billsIsLoading || billsIsFetching) && !hasBillHistoryError

  return (
    <Stack spacing={1}>
      <Typography variant="h3">{t("editBill.history.title")}</Typography>
      {hasBillHistoryError ? (
        <Stack spacing={1}>
          <DataGridRecordCount />
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => {
                  billsRefetch().catch(() => undefined)
                }}
                size="small"
              >
                {t("button.retry")}
              </Button>
            }
            severity="error"
          >
            {t("editBill.history.error")}
          </Alert>
        </Stack>
      ) : (
        <Box sx={{ height: 280 }}>
          <NzDataGrid
            aria-label={t("editBill.history.ariaLabel")}
            columns={historyColumns}
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            getRowId={(row: BillHistoryGridRow) => row.id}
            loading={historyGridLoading}
            rows={historyRows}
            slotProps={{
              toolbar: {
                isRecordCountLoading: historyGridLoading,
                recordCount: billsData?.length,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              toolbar: DataGridToolbar as GridSlots["toolbar"],
            }}
          />
        </Box>
      )}
    </Stack>
  )
}
